// /swap (docs/08-app-design.md 5.5): the form, the live quote, the review
// panel and the confirm state machine. The quote refreshes while the panel
// is open and says so; no key exists until Confirm. Confirm signs exactly
// the plan on screen (R41): a quote too old to sign is fetched again without
// a key, shown with "Quote updated", and needs a new Confirm; a refresh
// answer that arrives once Confirm has started is dropped. A key that needs
// a refresh gets it before the action, here, and the form comes back (R44).
// After Confirm, one of four outcomes (R45): landed, with the receipt read
// from the transaction itself (R46); not sent or failed on chain, with the
// key refresh running right here; or unknown, checked again on demand.
// A fee reserve refusal keeps its InsufficientGas.movable beside its cost, so
// the review points to this device's older reserve when it can be moved
// (docs/18-one-fee-reserve.md section 6).

import { type ActionProgress, ActionFailed, ActionUnknown, type ChainState, type CostEstimate, type Engine, InsufficientGas, type OlderReserve, QUOTE_MAX_AGE_MS, QuoteStale, RefreshRequired, type SwapPlan, type SwapRequest, type SwapResult, describe, feeSideOfMints, retirementOf } from '../engine';
import { WSOL_MINT, ataAddress, lstOf, maxInputForBalance, protocolFee } from '@thevault/sdk';
import { TOKEN_PROGRAM } from '@thevault/sdk/canary';
import type { VaultSigner } from '@thevault/sdk';
import { type FormEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { KeyRefresh } from '../components/KeyRefresh';
import { Address, Amount, Button, Field, Notice, StatusLine } from '../components/primitives';
import { REVIEW_GROUPS, type ReviewRow, ReviewPanel } from '../components/Review';
import { Shell } from '../components/Shell';
import { SceneCard } from '../components/SceneWindow';
import { type TokenAccountInfo, toBase58 } from '../rpc';
import { session, useSession } from '../session';
import { FEE_PERCENT } from '../site/facts';
import { KNOWN, USDC_MINT_B58, WSOL_MINT_B58, symbolOf } from '../tokens';
import { explorerBase, explorerUrl, parseUnits } from '../util';

const TOKEN_PROGRAM_B58 = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
const TOKEN_2022_B58 = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';
const REQUOTE_MS = 20_000;
/** The swap fee as the public facts state it ("0.30%"), spaced as the app's running text writes percentages ("0.30 %"). */
const FEE_TEXT = FEE_PERCENT.replace('%', ' %');

/**
 * What the vault protects and what it does not, when a liquid staking token is on either side (docs/19-staking-plan.md
 * section 3.6, the owner's draft copy): the token is held like any other, the pool, its operators and its validators
 * are outside the vault, and nothing here promises a yield.
 */
function lstNotice(inMint: string, outMint: string): ReactNode {
  const lst = lstOf(inMint) ?? lstOf(outMint);
  if (!lst) return null;
  const s = lst.symbol;
  return (
    <Notice>
      <p className="lst-notice" data-lst={s}>
        Your {s} is held in your vault, so no stolen key can move it. The staking behind it is run by the {s} stake pool: its program, its operators&rsquo; keys and the validators it stakes with are outside your vault&rsquo;s protection, and if they fail, the token can lose value. The vault keeps the token; it does not promise any yield. Swapping in and out costs the vault&rsquo;s {FEE_TEXT} fee each way, plus the market price difference.
      </p>
    </Notice>
  );
}

interface Holding {
  mint: string;
  amount: bigint;
  decimals: number;
}

async function loadHoldings(engine: Engine): Promise<Holding[]> {
  const out: Holding[] = [];
  const wsolAta = await ataAddress(engine.vaultAddressBytes, WSOL_MINT, TOKEN_PROGRAM);
  out.push({ mint: WSOL_MINT_B58, amount: await engine.tokenAmount(wsolAta), decimals: 9 });
  let rows: TokenAccountInfo[] = [];
  try {
    rows = (await Promise.all([engine.rpc.getTokenAccountsByOwner(engine.info.address, TOKEN_PROGRAM_B58), engine.rpc.getTokenAccountsByOwner(engine.info.address, TOKEN_2022_B58)])).flat();
  } catch {
    /* an RPC without the token index: only wSOL is offered */
  }
  for (const r of rows) {
    if (r.mint === WSOL_MINT_B58 || r.amount === 0n) continue;
    let decimals = KNOWN[r.mint]?.decimals ?? 0;
    if (!KNOWN[r.mint]) {
      try {
        decimals = await engine.decimalsOf(r.mint);
      } catch {
        /* raw units */
      }
    }
    out.push({ mint: r.mint, amount: r.amount, decimals });
  }
  return out;
}

export function Swap() {
  const { settings } = useSession();
  const engine = session.engine();
  const [state, setState] = useState<ChainState | null>(null);
  const [signer, setSigner] = useState<VaultSigner | null>(null);
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [inMint, setInMint] = useState(WSOL_MINT_B58);
  const [outMint, setOutMint] = useState(USDC_MINT_B58);
  const [otherOut, setOtherOut] = useState('');
  const [amountText, setAmountText] = useState('');
  // Set by MAX, cleared by typing: while set, the amount follows the fee side and the holdings.
  const [maxed, setMaxed] = useState(false);
  const [slippageBps, setSlippageBps] = useState(50);
  const [bounded, setBounded] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [plan, setPlan] = useState<SwapPlan | null>(null);
  const [cost, setCost] = useState<CostEstimate | null>(null);
  // The refusal's older reserve reading when `cost` came from InsufficientGas (docs/18 section 6); undefined for an estimate.
  const [costMovable, setCostMovable] = useState<OlderReserve | null | undefined>(undefined);
  const [updated, setUpdated] = useState(false);
  const [staleNote, setStaleNote] = useState(false);
  const [progress, setProgress] = useState<ActionProgress | null>(null);
  const [result, setResult] = useState<SwapResult | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [unknown, setUnknown] = useState<{ signature: string; detail: string; note: string | null } | null>(null);
  const [refreshNeeded, setRefreshNeeded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const confirming = useRef(false);
  // Every quote the panel may adopt belongs to a generation; a refresh answer from an older generation (issued before Confirm or before a reset) is dropped (R41).
  const generation = useRef(0);

  const reload = useCallback(async () => {
    try {
      const s = await engine.readChain();
      setState(s);
      if (s.kind === 'vault') {
        const sg = await engine.signerFor(s);
        setSigner(sg);
        let keys = await engine.keyStatus(sg);
        // Records left unknown by an earlier attempt are checked before anything else (R45); what they resolve to may itself call for a refresh.
        if (keys.unresolved.length) {
          await engine.reconcile(sg);
          keys = await engine.keyStatus(sg);
        }
        setRefreshNeeded(keys.refreshReason);
      }
      setHoldings(await loadHoldings(engine));
    } catch (e) {
      setError(describe(e));
    }
  }, [engine]);
  useEffect(() => {
    void reload();
  }, [reload]);

  const from = holdings.find((h) => h.mint === inMint);
  const inDecimals = from?.decimals ?? KNOWN[inMint]?.decimals ?? 9;
  const effectiveOut = outMint === 'other' ? otherOut.trim() : outMint;
  const amount = parseUnits(amountText, inDecimals);
  // The pair decides which side pays the treasury fee (program/src/fee.rs): when it is the input (a USDC or USDT
  // input, or SOL into anything but a stablecoin), the fee is paid on top of the amount, so both must fit.
  const side = feeSideOfMints(inMint, effectiveOut);
  const inFee = side === 'input' && amount !== null ? protocolFee(amount) : 0n;
  const needed = amount === null ? null : amount + inFee;
  const canQuote = !!from && amount !== null && needed !== null && amount > 0n && needed <= from.amount && effectiveOut.length >= 32 && effectiveOut !== inMint && !quoting;
  const feeDoesNotFit = side === 'input' && !!from && amount !== null && needed !== null && amount > 0n && amount <= from.amount && needed > from.amount;
  // MAX leaves room for a fee paid from the input: the largest amount A with A + fee(A) within what is available.
  const maxAmount = from ? maxInputForBalance(side, from.amount) : 0n;
  useEffect(() => {
    if (maxed && from) setAmountText(formatPlain(maxAmount, from.decimals));
  }, [maxed, maxAmount, from]);

  const request = (): SwapRequest => ({ inMint, outMint: effectiveOut, amount: amount!, slippageBps, bounded });

  /** Show a plan and estimate what it costs the fee reserve; the estimate of an older generation is dropped too. */
  const adopt = (fresh: SwapPlan, gen: number) => {
    setPlan(fresh);
    setCost(null);
    setCostMovable(undefined);
    void engine
      .swapCost(fresh)
      .then((c) => {
        if (gen === generation.current) setCost(c);
      })
      .catch((err) => {
        if (gen === generation.current) setQuoteError(describe(err));
      });
  };

  const quote = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canQuote) return;
    setQuoting(true);
    setQuoteError(null);
    setResult(null);
    setProgress(null);
    setStaleNote(false);
    const gen = ++generation.current;
    try {
      adopt(await engine.quoteSwap(request(), signer ?? undefined), gen);
      setUpdated(false);
    } catch (err) {
      setQuoteError(describe(err));
      setPlan(null);
    } finally {
      setQuoting(false);
    }
  };

  // The quote refreshes while the panel is open (docs/08 5.5 step 2); never during or after confirm, and an answer that
  // comes back after Confirm has started is dropped rather than shown (R41).
  useEffect(() => {
    if (!plan || progress) return;
    const id = setInterval(() => {
      if (confirming.current) return;
      const gen = generation.current;
      void engine
        .quoteSwap(plan.request, signer ?? undefined)
        .then((fresh) => {
          if (confirming.current || gen !== generation.current) return;
          adopt(fresh, gen);
          setUpdated(true);
          setStaleNote(false);
          setQuoteError(null);
        })
        .catch((err) => {
          if (gen === generation.current) setQuoteError(describe(err));
        });
    }, REQUOTE_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, progress, engine, signer]);

  const confirm = async () => {
    if (!plan || !signer || confirming.current) return;
    confirming.current = true;
    // From here on the plan on screen is the plan being signed: no refresh may replace it.
    const gen = ++generation.current;
    setFailed(null);
    setUnknown(null);
    setUpdated(false);
    setStaleNote(false);
    setQuoteError(null);
    try {
      const r = await engine.confirmSwap(signer, plan, setProgress);
      setResult(r);
      await reload();
    } catch (err) {
      if (err instanceof QuoteStale) {
        // Too old to sign: fetched again without a key, shown, and confirmed again by the user, never by the app.
        setProgress(null);
        try {
          adopt(await engine.quoteSwap(plan.request, signer), gen);
          setUpdated(true);
          setStaleNote(true);
        } catch (e2) {
          setQuoteError(describe(e2));
        }
      } else if (err instanceof RefreshRequired) {
        // The key needs a refresh first (R44): it runs here, then the form comes back.
        setProgress(null);
        setRefreshNeeded(err.reason);
      } else if (err instanceof InsufficientGas) {
        setProgress(null);
        setCost(err.cost);
        setCostMovable(err.movable);
      } else if (err instanceof ActionFailed) {
        // A key was burned: the refresh runs here, then the form comes back with a fresh quote.
        setFailed(`${err.message} ${err.detail}`);
      } else if (err instanceof ActionUnknown) {
        setUnknown({ signature: err.signature, detail: err.detail, note: null });
      } else {
        // Refused before any key existed (the program check, a stale slot, an unresolved record): the panel stays for another try.
        setProgress({ step: 'rejected', error: describe(err) });
      }
    } finally {
      confirming.current = false;
    }
  };

  /** An unknown outcome, checked against the chain (R45): landed, failed, or still unknown. */
  const checkUnknown = async () => {
    if (!unknown || !signer || !plan) return;
    try {
      await engine.reconcile(signer);
      const record = (await engine.history(signer)).find((r) => r.txSignature === unknown.signature);
      if (record?.status === 'landed') {
        const { receipt, receiptNote } = await engine.swapReceiptFor(unknown.signature, plan);
        setResult({ signature: unknown.signature, leaf: record.leaf, transactionSlot: 0, readback: null, receipt, receiptNote });
        setProgress({ step: 'landed', leaf: record.leaf, signature: unknown.signature });
        setUnknown(null);
        await reload();
      } else if (record?.status === 'burned') {
        // The ledger's reason decides the words (R56): only a confirmed error is "failed on chain"; an unconfirmed expiry is "expired".
        const retired = retirementOf(record);
        setUnknown(null);
        setProgress({ step: retired.step, leaf: record.leaf, signature: unknown.signature, error: retired.sentence });
        setFailed(retired.sentence);
      } else {
        setUnknown({ ...unknown, note: 'Still unknown: the network has no confirmation yet. Check again in a moment.' });
      }
    } catch (e) {
      setUnknown({ ...unknown, note: describe(e) });
    }
  };

  const reset = () => {
    generation.current++;
    setPlan(null);
    setCost(null);
    setCostMovable(undefined);
    setProgress(null);
    setResult(null);
    setFailed(null);
    setUnknown(null);
    setUpdated(false);
    setStaleNote(false);
  };

  const outSymbol = plan ? symbolOf(plan.request.outMint) : symbolOf(effectiveOut);
  const keyLine = signer ? `tree ${signer.treeIndex}` : 'this tree';
  const detail = state?.kind === 'vault' ? `KEY TREE ${state.snap.treeIndex}` : state ? state.kind.toUpperCase() : 'READING';
  const addrBase = explorerBase(settings.rpcUrl, 'address');

  /**
   * The panel's largest figure and rows (docs/08 5.5 step 2), by the side that pays the treasury fee. Output side,
   * as before: the fee comes out of what arrives, so the largest figure is the minimum after the fee. Input side: the
   * fee is paid in the input token on top of what the swap spends, "Spend at most" is the amount plus that fee, the
   * fee is its own line in the input token, and the largest figure is the output minimum, which arrives whole.
   */
  const panelFigures = (plan: SwapPlan): { net: { label: string; value: ReactNode; detail?: ReactNode }; bound: { label: string; value: ReactNode }; rows: ReviewRow[] } => {
    const inSymbol = symbolOf(plan.request.inMint);
    const out = (raw: bigint, size?: 'large') => <Amount raw={raw} decimals={plan.outDecimals} symbol={outSymbol} maxFraction={plan.outDecimals} {...(size ? { size } : {})} />;
    const inn = (raw: bigint) => <Amount raw={raw} decimals={plan.inDecimals} symbol={inSymbol} maxFraction={plan.inDecimals} />;
    const treasury = <Address value={plan.feeAccount} kind="treasury" explorer={addrBase ? explorerUrl(addrBase, '')! : undefined} />;
    const routerRow: ReviewRow = { group: REVIEW_GROUPS.terms, label: 'Estimated receive', value: out(plan.quotedOut), detail: `Slippage: ${(plan.request.slippageBps / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%. The minimum above is what the program enforces.` };
    const routeRow: ReviewRow = { group: REVIEW_GROUPS.route, label: 'Route', value: `${plan.routeLabels.join(' → ')}, Jupiter executes`, detail: plan.request.bounded ? 'bounded: the route may change, the bounds may not' : 'exact: this route and no other' };
    // Where the output lands: the vault's own account for that token, the one the program checks after the route (expect.outAta).
    const destinationRow: ReviewRow = {
      group: REVIEW_GROUPS.terms,
      label: 'Destination',
      value: 'Your vault',
      detail: (
        <>
          its {plan.request.outMint === WSOL_MINT_B58 ? 'wrapped SOL' : outSymbol} account <Address value={toBase58(plan.expect.outAta)} />
        </>
      ),
    };
    const bound = { label: 'Spend at most', value: inn(plan.maxIn) };
    if (plan.feeSide === 'input') {
      const feeSymbol = symbolOf(plan.feeMint);
      return {
        net: {
          label: 'Receive at least, in full',
          value: out(plan.netMin, 'large'),
          detail: <>the treasury fee is paid in {feeSymbol}, on top of what the swap spends, so none of this goes to the treasury</>,
        },
        rows: [
          {
            group: REVIEW_GROUPS.terms,
            label: 'Spend at most',
            value: inn(plan.maxIn),
            detail: (
              <>
                {inn(plan.request.amount)} for the swap and {inn(plan.fee)} for the treasury fee
              </>
            ),
          },
          routerRow,
          destinationRow,
          {
            group: REVIEW_GROUPS.costs,
            label: `Swap fee, paid in ${feeSymbol}`,
            value: <Amount raw={plan.fee} decimals={plan.feeDecimals} symbol={feeSymbol} maxFraction={plan.feeDecimals} />,
            detail: (
              <>
                {FEE_TEXT} of what the swap spends, taken from the vault's {feeSymbol} after the swap, to the treasury {treasury}
              </>
            ),
          },
          routeRow,
        ],
        bound,
      };
    }
    return {
      net: {
        label: `Receive at least, after the fee`,
        value: out(plan.netMin, 'large'),
        detail: (
          <>
            gross {out(plan.grossMin)}, of which fee {out(plan.fee)} to the treasury
          </>
        ),
      },
      rows: [
        { group: REVIEW_GROUPS.terms, label: 'Spend at most', value: inn(plan.maxIn) },
        routerRow,
        destinationRow,
        {
          group: REVIEW_GROUPS.costs,
          label: 'Swap fee',
          value: out(plan.fee),
          detail: <>{FEE_TEXT} of what actually arrives: this is the fee at the minimum, and more if the route delivers more. To the treasury {treasury}</>,
        },
        routeRow,
      ],
      bound,
    };
  };
  const figures = plan ? panelFigures(plan) : null;

  return (
    <Shell number="02" title="Swap" detail={detail}>
      {error ? (
        <StatusLine state="failed" word="Could not read the chain.">
          {error}
        </StatusLine>
      ) : null}
      {state && state.kind !== 'vault' ? (
        <Notice label="Not available">
          <p className="ink">{state.kind === 'absent' || state.kind === 'prefunded' ? 'Activate the vault from the overview before swapping.' : 'The vault is not in a state that can swap.'}</p>
        </Notice>
      ) : null}

      {refreshNeeded && signer ? (
        <Notice label="Key refresh" tone="warn">
          <KeyRefresh
            engine={engine}
            signer={signer}
            reason={`${refreshNeeded} It runs now, before anything else; the form follows.`}
            onDone={() => {
              setRefreshNeeded(null);
              reset();
              void reload();
            }}
          />
        </Notice>
      ) : null}

      <div className="task">
        <div className="task-main">
          <div className="composer-head">
            <h1>Swap</h1>
            <span className="dim">{plan ? 'REVIEW' : 'INSIDE YOUR VAULT'}</span>
          </div>

          {!plan && !failed && !refreshNeeded ? (
            <form className="composer" onSubmit={(e) => void quote(e)}>
              <Field id="swap-from" label="You pay" help={from ? undefined : 'Nothing swappable yet. SOL becomes swappable from the overview.'}>
                <select id="swap-from" className="input" value={inMint} onChange={(e) => setInMint(e.target.value)}>
                  {holdings.map((h) => (
                    <option key={h.mint} value={h.mint}>
                      {symbolOf(h.mint)}
                    </option>
                  ))}
                  {holdings.length === 0 ? <option value={WSOL_MINT_B58}>SOL (wrapped)</option> : null}
                </select>
              </Field>
              <Field
                id="swap-amount"
                label="Amount"
                help={
                  from ? (
                    <>
                      Available: <Amount raw={from.amount} decimals={from.decimals} symbol={symbolOf(from.mint)} />
                      {side === 'input' ? <span className="fee-note"> The treasury fee, {FEE_TEXT} of what the swap spends, is paid in {symbolOf(from.mint)} on top of the amount.</span> : null}
                    </>
                  ) : undefined
                }
              >
                <div className="field-row">
                  <input
                    id="swap-amount"
                    className="input"
                    inputMode="decimal"
                    value={amountText}
                    onChange={(e) => {
                      setMaxed(false);
                      setAmountText(e.target.value);
                    }}
                    placeholder="0.00"
                    autoComplete="off"
                  />
                  <Button
                    variant="ghost"
                    small
                    type="button"
                    disabled={!from}
                    onClick={() => {
                      if (!from) return;
                      setMaxed(true);
                      setAmountText(formatPlain(maxAmount, from.decimals));
                    }}
                  >
                    Max
                  </Button>
                </div>
              </Field>
              {feeDoesNotFit && from && needed !== null ? (
                <p className="fee-room">
                  With the treasury fee of <Amount raw={inFee} decimals={from.decimals} symbol={symbolOf(from.mint)} maxFraction={from.decimals} /> on top, this swap needs <Amount raw={needed} decimals={from.decimals} symbol={symbolOf(from.mint)} maxFraction={from.decimals} />, more than the vault holds. At most <Amount raw={maxAmount} decimals={from.decimals} symbol={symbolOf(from.mint)} maxFraction={from.decimals} /> can be swapped; Max fills that in.
                </p>
              ) : null}
              <Field id="swap-to" label="To">
                <select id="swap-to" className="input" value={outMint} onChange={(e) => setOutMint(e.target.value)}>
                  {Object.entries(KNOWN)
                    .filter(([mint]) => mint !== inMint && !(lstOf(inMint) && lstOf(mint)))
                    .map(([mint, t]) => (
                      <option key={mint} value={mint}>
                        {t.symbol}
                      </option>
                    ))}
                  <option value="other">Another mint…</option>
                </select>
              </Field>
              {outMint === 'other' ? (
                <Field id="swap-to-mint" label="Mint address">
                  <input id="swap-to-mint" className="input" value={otherOut} onChange={(e) => setOtherOut(e.target.value)} autoComplete="off" spellCheck={false} />
                </Field>
              ) : null}
              {lstNotice(inMint, effectiveOut)}
              <p className="composer-line">
                Slippage {(slippageBps / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%<span aria-hidden="true"> · </span>
                <span className="dim">change it under Advanced</span>
              </p>
              <details>
                <summary className="eyebrow dim">Advanced</summary>
                <Field id="swap-slippage" label="Slippage, basis points" help="The quote's minimum is this far below the estimate. 50 is half a percent.">
                  <input id="swap-slippage" className="input" type="number" min={1} max={1000} value={slippageBps} onChange={(e) => setSlippageBps(Math.max(1, Math.min(1000, Number(e.target.value) || 1)))} />
                </Field>
                <label className="checkbox-row">
                  <input type="checkbox" checked={bounded} onChange={(e) => setBounded(e.target.checked)} />
                  <span>Bounded route: if the route fails, re-send the same signature with a fresh route until it expires</span>
                </label>
                <p>Off, the signature binds the exact route you review. On, it binds the venue program and your accounts, and the route may be swapped for another that keeps the same bounds.</p>
              </details>
              {engine.sessionExclusions.size ? <p>Excluded this session after a rejection: {[...engine.sessionExclusions].join(', ')}.</p> : null}
              {quoteError ? (
                <StatusLine state="failed" word="No quote.">
                  {quoteError}
                </StatusLine>
              ) : null}
              <Button type="submit" block disabled={!canQuote} working={quoting}>
                {quoting ? 'Quoting' : 'Get a quote'}
              </Button>
            </form>
          ) : null}

      {plan && !refreshNeeded ? (
        <ReviewPanel
          engine={engine}
          rpcUrl={settings.rpcUrl}
          keyLine={keyLine}
          rotates={plan.rotates}
          cost={cost}
          movable={costMovable}
          progress={progress}
          updated={updated}
          quote={{
            at: plan.quotedAt,
            note: progress
              ? `A quote older than ${QUOTE_MAX_AGE_MS / 1000} s is fetched again at Confirm, without using a key.`
              : `It refreshes every ${REQUOTE_MS / 1000} s while you review, and is fetched again at Confirm if older than ${QUOTE_MAX_AGE_MS / 1000} s, without using a key.`,
          }}
          title="Review your swap"
          confirmLabel="Confirm swap"
          onConfirm={() => void confirm()}
          onCancel={result || failed || unknown ? undefined : reset}
          onRecheck={() => adopt(plan, ++generation.current)}
          disabled={!signer || !!failed || !!unknown}
          net={figures!.net}
          bound={figures!.bound}
          rows={figures!.rows}
        >
          {staleNote && !progress ? (
            <StatusLine state="neutral" word="Quote updated.">
              The reviewed quote was older than 25 s, so a fresh one was fetched without using a key. Review the figures above and confirm again.
            </StatusLine>
          ) : null}
          {quoteError && !progress && !staleNote ? (
            <StatusLine state="neutral" word="Quote not refreshed.">
              {quoteError} A quote older than 25 s is fetched again at Confirm, without using a key, and shown for a new confirmation.
            </StatusLine>
          ) : null}
          {plan.skipped.length ? (
            <details>
              <summary className="eyebrow dim">{plan.skipped.length} route sizes skipped</summary>
              <ul className="link-list">
                {plan.skipped.map((s) => (
                  <li key={s.maxAccounts}>
                    {s.maxAccounts} accounts: {s.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {unknown ? (
            <Notice label="Outcome unknown" tone="warn">
              <p className="ink">{unknown.note ?? `${unknown.detail} Nothing is retired and nothing new is signed until the outcome is known.`}</p>
              <div className="actions">
                <Button small onClick={() => void checkUnknown()}>
                  Check again
                </Button>
              </div>
            </Notice>
          ) : null}
          {result ? (
            <Notice label="Settled" tone="accent">
              {result.receipt && result.receipt.feeSide === 'input' ? (
                <p className="ink">
                  {/* Input side (R46): the output arrived whole; the fee left the vault's input account on top of what the swap spent. */}
                  Received <Amount raw={result.receipt.received} decimals={plan.outDecimals} symbol={outSymbol} maxFraction={plan.outDecimals} />, the whole output.{' '}
                  {result.receipt.spent !== undefined ? (
                    <>
                      The swap spent <Amount raw={result.receipt.spent} decimals={plan.inDecimals} symbol={symbolOf(plan.request.inMint)} maxFraction={plan.inDecimals} />, and the treasury's fee of{' '}
                    </>
                  ) : (
                    <>The treasury's fee of </>
                  )}
                  <Amount raw={result.receipt.protocolDebit} decimals={plan.feeDecimals} symbol={symbolOf(plan.feeMint)} maxFraction={plan.feeDecimals} /> was paid from the vault's {symbolOf(plan.feeMint)}
                  {result.receipt.spent !== undefined ? ' on top of that' : ''}
                  {result.receipt.withheld > 0n ? (
                    <>
                      , of which <Amount raw={result.receipt.withheld} decimals={plan.feeDecimals} symbol={symbolOf(plan.feeMint)} maxFraction={plan.feeDecimals} /> was withheld by the token's own transfer fee
                    </>
                  ) : null}
                  . {result.receipt.spent !== undefined ? 'All three figures are' : 'Both figures are'} this transaction's own balance changes.
                </p>
              ) : result.receipt ? (
                <p className="ink">
                  Received <Amount raw={result.receipt.received} decimals={plan.outDecimals} symbol={outSymbol} maxFraction={plan.outDecimals} />. The route delivered <Amount raw={result.receipt.gross} decimals={plan.outDecimals} symbol={outSymbol} maxFraction={plan.outDecimals} />, and the treasury's fee of{' '}
                  <Amount raw={result.receipt.protocolDebit} decimals={plan.outDecimals} symbol={outSymbol} maxFraction={plan.outDecimals} /> was taken from that
                  {result.receipt.withheld > 0n ? (
                    <>
                      , of which <Amount raw={result.receipt.withheld} decimals={plan.outDecimals} symbol={outSymbol} maxFraction={plan.outDecimals} /> was withheld by the token's own transfer fee
                    </>
                  ) : null}
                  . All three figures are this transaction's own balance changes.
                </p>
              ) : (
                <p className="ink">{result.receiptNote}</p>
              )}
              {result.readback ? <p>{result.readback}</p> : null}
              <div className="actions">
                <Button small onClick={reset}>
                  Swap again
                </Button>
              </div>
            </Notice>
          ) : null}
        </ReviewPanel>
      ) : null}

      {failed && signer ? (
        <Notice label="Key refresh" tone="warn">
          <KeyRefresh
            engine={engine}
            signer={signer}
            reason={`${failed} The vault refreshes its key now, then the form comes back with a fresh quote.`}
            onDone={() => {
              reset();
              void reload();
            }}
          />
        </Notice>
      ) : null}
        </div>
        <aside className="task-side" aria-label="Your swappable holdings">
          <SceneCard scene="swap" />
          <div className="panel">
            <span className="eyebrow">In your vault</span>
            {holdings.length ? (
              holdings.map((h) => (
                <div className="side-row" key={h.mint}>
                  <span>
                    <span className={`token-glyph ${KNOWN[h.mint] ? `t-${KNOWN[h.mint]!.symbol.toLowerCase()}` : ''}`.trim()} aria-hidden="true">
                      {symbolOf(h.mint).slice(0, 1)}
                    </span>
                    {symbolOf(h.mint)}
                  </span>
                  <Amount raw={h.amount} decimals={h.decimals} symbol={symbolOf(h.mint)} maxFraction={Math.min(h.decimals, 4)} />
                </div>
              ))
            ) : (
              <p>Nothing swappable yet.</p>
            )}
            <p className="activity-note">Native SOL becomes swappable from the Overview.</p>
          </div>
          <div className="panel">
            <span className="eyebrow">How a swap works here</span>
            <p className="activity-note">A supported Jupiter route runs outside the vault. The program checks that no more than you approved was spent and at least the minimum came back, or nothing moves. The {FEE_TEXT} swap fee is shown on its own line before you confirm.</p>
          </div>
        </aside>
      </div>
    </Shell>
  );
}

function formatPlain(raw: bigint, decimals: number): string {
  const s = raw.toString().padStart(decimals + 1, '0');
  const whole = s.slice(0, s.length - decimals);
  const frac = s.slice(s.length - decimals).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}

export { toBase58 };
